/**
 * The state behind "Create reason" from a picker (FB-1013): an unsaved reason,
 * what keeps it from being saved, and the save itself.
 */
import { apiHelpers } from "../../lib/api";
import { createSharedReason } from "../../hooks/useReasons";
import { hasReasonTitle } from "../editor/reasonOptions";
import type { RedactionReason } from "../../types";
import { TITLE_MAX_LENGTH, isValidReasonUrl } from "../../utils/reasonLegal";

export interface ReasonDraft {
  title: string;
  description: string;
  citation: string;
  legal_text: string;
  url: string;
}

export function emptyReasonDraft(title = ""): ReasonDraft {
  return { title: title.slice(0, TITLE_MAX_LENGTH), description: "", citation: "", legal_text: "", url: "" };
}

/** Why the draft can't be saved yet, or null when it can. */
export function reasonDraftProblem(draft: ReasonDraft, reasons: readonly Pick<RedactionReason, "title">[]): string | null {
  const title = draft.title.trim();
  if (!title) return "Enter a title.";
  // Pickers select a reason by its title, so a second one with the same title
  // could never be told apart.
  if (hasReasonTitle(reasons, title)) return `A reason titled “${title}” already exists. Pick it from the list.`;
  if (!isValidReasonUrl(draft.url)) return "Link must start with http:// or https://";
  return null;
}

/** Create the reason; an API failure comes back as the message to show. */
export async function saveReasonDraft(
  draft: ReasonDraft,
): Promise<{ reason: RedactionReason; error?: undefined } | { reason?: undefined; error: string }> {
  try {
    const reason = await createSharedReason({
      title: draft.title.trim(),
      description: draft.description.trim(),
      citation: draft.citation.trim(),
      legal_text: draft.legal_text.trim(),
      url: draft.url.trim(),
    });
    return { reason };
  } catch (err) {
    return { error: apiHelpers.handleError(err) };
  }
}
